// Where `/static/**` lives, for the consumers that are not served by the proxy.
//
// ⚠️ THE SAME SPLIT AS `configureApi`, AND FOR THE SAME REASON (CLAUDE.md, `packages/api`).
// Every ported screen references its images the way upstream does —
// `/static/assets/img/...` — which is right in the SPA: the proxy serves `/static/**` from
// the tenant, so the bare path reaches the same bytes production serves and the pixel diff
// holds. In an EXTENSION PAGE the origin is `chrome-extension://<id>`, where that path
// resolves against the extension itself and 404s: the login logo, the status icons and the
// enable-cookies GIFs all render as nothing, with no failed request anyone looks at.
//
// So the base is configurable and defaults to empty. Unconfigured, `asset()` returns its
// argument unchanged — the SPA keeps the exact string it had, and the baseline diff with it
// — and `apps/shell` calls `configureAssets({ baseUrl })` with the tenant it already
// resolves for `configureApi`.
let baseUrl = ''

export interface AssetConfig {
  /** Origin the `/static/**` paths resolve against, e.g. `https://woo.io`. */
  baseUrl: string
}

export function configureAssets(config: AssetConfig): void {
  // A trailing slash would produce `https://host//static/...`, which most servers answer
  // and some proxies do not — and which would differ from the SPA's string for no reason.
  baseUrl = config.baseUrl.replace(/\/+$/, '')
}

/** The base currently configured, for a caller that has to build a URL itself. */
export function assetBaseUrl(): string {
  return baseUrl
}

/**
 * Resolve a ported asset path against the configured base.
 *
 * ⚠️ CALL IT AT RENDER TIME, NOT AT MODULE LOAD. Several of these paths live in a
 * module-level record (`StatusIndications`, the source logos), which is evaluated when the
 * module is imported — before the shell has read the tenant out of storage. Rebasing there
 * would bake the empty default in; the call sites are therefore in the JSX.
 *
 * Only root-relative paths are rebased. Anything already absolute — an `https://` URL, a
 * `data:` URI — is returned untouched, because a caller that already knows where its asset
 * lives is not guessing.
 */
export function asset(path: string): string {
  if (baseUrl.length === 0 || !path.startsWith('/')) return path
  return `${baseUrl}${path}`
}
